import {
  keepPreviousData,
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

import { conns } from '@cfg/axiosConfig.tsx';
import { toAnalyticsContext, type BehaviorAnalyticsContext } from '@tools/behaviorAnalytics.ts';

export type PosItemType = 'product' | 'service' | 'subscription_plan';
export type PosServiceKind = 'simple' | 'appointment' | 'group';

export interface PosItemPrice {
  value: number;
  currency: string;
  valid_from?: string;
  valid_to?: string;
  tax_rate?: number | null;
}

export interface PosItemChild {
  id: number;
  parent_id?: number | null;
  name: string;
  sku?: string | null;
  barcode?: string | null;
  item_type: PosItemType;
  price?: PosItemPrice | null;
  category_ids: number[];
  flags: PosItemFlags;
  service_detail?: PosItemServiceDetail | null;
  service_kind?: PosServiceKind | null;
  thumbnail_url?: string | null;
}

export interface PosItemProfessional {
  id: number;
  full_name: string;
  is_active: boolean;
  allows_overbooking?: boolean;
}

export interface PosItemFlags {
  is_active: boolean;
  is_for_sale: boolean;
  is_visible_web: boolean;
  is_digital: boolean;
  is_insumo: boolean;
  manages_stock?: boolean | null;
}

export interface PosItemServiceDetail {
  is_subscription: boolean;
  is_appointment_based: boolean;
  allow_quantity_gt1: boolean;
  history_enabled: boolean;
  default_duration?: number | null;
  default_professional_id?: number | null;
  default_space_id?: number | null;
}

export interface PosItem {
  id: number;
  parent_id?: number | null;
  name: string;
  sku?: string | null;
  barcode?: string | null;
  item_type: PosItemType;
  price?: PosItemPrice | null;
  category_ids: number[];
  flags: PosItemFlags;
  service_detail?: PosItemServiceDetail | null;
  service_kind?: PosServiceKind | null;
  tags?: string[] | null;
  is_parent?: boolean;
  children?: PosItemChild[] | null;
  professionals?: PosItemProfessional[] | null;
  thumbnail_url?: string | null;
}

export interface PosItemsPagination {
  page: number;
  page_size: number;
  has_next: boolean;
  total?: number | null;
  applied_category_ids: number[];
}

export interface PosItemsSearchResponse {
  results: PosItem[];
  pagination: PosItemsPagination;
}

export interface PosItemSuggestion extends PosItem {
  score: number | string;
  event_count: number;
  last_used_at?: string | null;
  variant_id?: number | null;
}

export interface PosItemSuggestionsResponse {
  context: Record<string, unknown>;
  items: PosItemSuggestion[];
  fallback_level: string;
  computed_at?: string | null;
}

export interface PosItemsSearchParams {
  q?: string;
  itemType?: PosItemType;
  allowedItemTypes?: PosItemType[];
  serviceKind?: PosServiceKind;
  includeSubscriptionPlans?: boolean;
  categoryIds?: number[];
  sku?: string;
  tags?: string[];
  rootsOnly?: boolean;
  page?: number;
  pageSize?: number;
}

export interface PosItemSuggestionsParams {
  context: BehaviorAnalyticsContext;
  itemType?: PosItemType;
  allowedItemTypes?: PosItemType[];
  serviceKind?: PosServiceKind;
  includeSubscriptionPlans?: boolean;
  limit?: number;
  excludeItemIds?: number[];
}

interface NormalizedParams {
  q?: string;
  itemType?: PosItemType;
  allowedItemTypes: PosItemType[];
  serviceKind?: PosServiceKind;
  includeSubscriptionPlans: boolean;
  categoryIds: number[];
  sku?: string;
  tags: string[];
  rootsOnly: boolean;
  page: number;
  pageSize: number;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;

const normalizeParams = (params: PosItemsSearchParams): NormalizedParams => {
  const trimmed = params.q?.trim();
  const sku = params.sku?.trim();
  return {
    q: trimmed ? trimmed : undefined,
    itemType: params.itemType ?? (params.allowedItemTypes?.length === 1 ? params.allowedItemTypes[0] : undefined),
    allowedItemTypes: [...(params.allowedItemTypes ?? [])].sort(),
    serviceKind: params.serviceKind,
    includeSubscriptionPlans: Boolean(params.includeSubscriptionPlans),
    categoryIds: params.categoryIds ? [...params.categoryIds].sort((a, b) => a - b) : [],
    sku: sku ? sku : undefined,
    tags: params.tags ? params.tags.map((tag) => tag.trim()).filter(Boolean).sort() : [],
    rootsOnly: Boolean(params.rootsOnly),
    page: params.page ?? DEFAULT_PAGE,
    pageSize: params.pageSize ?? DEFAULT_PAGE_SIZE,
  };
};

const buildSearchParams = (params: NormalizedParams): URLSearchParams => {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  if (params.itemType) search.set('item_type', params.itemType);
  if (params.serviceKind) search.set('service_kind', params.serviceKind);
  if (params.includeSubscriptionPlans) search.set('include_subscription_plans', 'true');
  params.categoryIds.forEach((id) => search.append('category_ids', String(id)));
  if (params.sku) search.set('sku', params.sku);
  params.tags.forEach((tag) => search.append('tags', tag));
  if (params.rootsOnly) search.set('roots_only', 'true');
  if (params.page !== DEFAULT_PAGE) search.set('page', String(params.page));
  if (params.pageSize !== DEFAULT_PAGE_SIZE) search.set('page_size', String(params.pageSize));
  return search;
};

const fetchPosItems = async (params: PosItemsSearchParams): Promise<PosItemsSearchResponse> => {
  const normalized = normalizeParams(params);
  const searchParams = buildSearchParams(normalized);
  const { data } = await conns<PosItemsSearchResponse>('erp:pos:items:search').get(searchParams);
  if (!normalized.allowedItemTypes.length) return data;
  return {
    ...data,
    results: data.results.filter((item) => normalized.allowedItemTypes.includes(item.item_type)),
  };
};

const buildSuggestionParams = (params: PosItemSuggestionsParams): URLSearchParams => {
  const context = toAnalyticsContext(params.context);
  const search = new URLSearchParams();
  Object.entries(context).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim()) {
      search.set(key, String(value));
    }
  });
  if (params.itemType) search.set('item_type', params.itemType);
  search.set('limit', String(params.limit ?? 15));
  const excludedIds = [...new Set(params.excludeItemIds ?? [])]
    .filter((id) => Number.isFinite(id))
    .sort((a, b) => a - b);
  excludedIds.forEach((id) => search.append('exclude_item_ids', String(id)));
  return search;
};

const fetchPosItemSuggestions = async (
  params: PosItemSuggestionsParams,
): Promise<PosItemSuggestionsResponse> => {
  const searchParams = buildSuggestionParams(params);
  const { data } = await conns<PosItemSuggestionsResponse>('erp:pos:items:suggestions').get(searchParams);
  return data;
};

type PosItemsSearchQueryOptions<TData> = Omit<
  UseQueryOptions<PosItemsSearchResponse, Error, TData>,
  'queryKey' | 'queryFn'
> & {
  keepPreviousData?: boolean;
};

export const usePosItemsSearch = <TData = PosItemsSearchResponse>(
  params: PosItemsSearchParams,
  options?: PosItemsSearchQueryOptions<TData>,
): UseQueryResult<TData, Error> => {
  const { keepPreviousData: shouldKeepPreviousData = false, placeholderData, ...queryOptions } = options ?? {};

  const resolvedPlaceholder = placeholderData ?? (shouldKeepPreviousData ? keepPreviousData : undefined);

  return useQuery<PosItemsSearchResponse, Error, TData>({
    queryKey: ['pos', 'items', normalizeParams(params)],
    queryFn: () => fetchPosItems(params),
    staleTime: 30_000,
    ...(resolvedPlaceholder ? { placeholderData: resolvedPlaceholder } : {}),
    ...queryOptions,
  });
};

export const usePosItemSuggestions = <TData = PosItemSuggestionsResponse>(
  params: PosItemSuggestionsParams,
  options?: Omit<UseQueryOptions<PosItemSuggestionsResponse, Error, TData>, 'queryKey' | 'queryFn'>,
): UseQueryResult<TData, Error> => {
  return useQuery<PosItemSuggestionsResponse, Error, TData>({
    queryKey: ['pos', 'items', 'suggestions', buildSuggestionParams(params).toString()],
    queryFn: () => fetchPosItemSuggestions(params),
    staleTime: 300_000,
    ...options,
  });
};

export { buildSearchParams, buildSuggestionParams, fetchPosItems, fetchPosItemSuggestions, normalizeParams };
